/* global PLATFORM, G */
import { asyncPipeSpread, asyncPipeSpreadIf } from 'lib/util'
import { hide, show } from 'lib/sequence/component/state/hidden'
import find from 'lib/sequence/component/children/find'
import {
  shouldShowReference,
  displayAvgFrequency,
  displayAvgUltrasonicIntensity,
  displayComment, displayReferenceValues,
  loadReferenceMeasurementData,
  mapLocation,
  mapProperties,
  mapQuantities,
  renderAcousticSpectrumChart,
  renderCavitationIntensityChart,
  renderMainFrequencyChart,
  returnArgs,
  displayReferenceToggle,
} from 'app/xEcoApm/action/measurement/detail'

/**
 * @typedef {Record<string, Gaia.Component.Spec|null>} Components
 */

/**
 * Hides the screen title.
 *
 * @param {Components} components {@link find} proxy of the current action component
 * @param {unknown[]} args                     additional arguments
 * @returns {Promise<[Components, ...[*]]>}
 */
const hideTitle = async (components, ...args) => {
  hide(components.title)

  return [components, ...args]
}

/**
 * Extracts `measuredData` from the `measurement` model attribute to be used by the next functions.
 *
 * @param {Gaia.AppModule.Spec} module  current module
 * @returns {function(Components, ...[*]): Promise<[Components, object, ...[*]]>}
 */
export const loadMeasurementData = module => async (components, ...args) => {
  const model = module[G.MODEL]
  const { measurement } = model[G.CHILDREN]
  const { measuredData } = measurement[G.DATA]

  return [components, measuredData, ...args]
}

/**
 * Hides the submit field.
 *
 * @param components
 * @param args
 * @returns {*[]}
 */
const hideSubmit = (components, ...args) => {
  hide(components.submit)

  return [components, ...args]
}

/**
 * Shows the screen header if current measurement is a reference, otherwise, it hides it.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayReferenceHeader = module => async (components, ...args) => {
  // model
  const { measurement } = module[G.MODEL][G.CHILDREN]
  const { reference } = measurement[G.DATA]

  const { header } = components

  reference
    ? show(header)
    : hide(header)

  return [components, ...args]
}

/**
 * Measurement Detail Action
 *
 * Calculates averages, prepares chart data and updates the UI with the deduced values.
 *
 * @param {Gaia.AppModule.Spec} module  the current module object composition
 * @returns {function(Gaia.Component.Spec): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  hideTitle,
  hideSubmit,
  displayReferenceHeader(module),
  asyncPipeSpreadIf(shouldShowReference)(
    loadReferenceMeasurementData(module),
  ),
  loadMeasurementData(module),
  displayReferenceToggle(module),
  displayReferenceValues,
  mapProperties(module),
  mapQuantities(module),
  mapLocation(module),
  displayAvgUltrasonicIntensity(module),
  displayAvgFrequency,
  displayComment(module),
  renderAcousticSpectrumChart,
  renderCavitationIntensityChart,
  renderMainFrequencyChart,
  returnArgs,
)(find(component), ...args)
